import type { ReportBrand } from '../voyage/source.js';
import type { CrewNotesSource } from './crew-notes.js';
import type { PdfOutput } from './pdf.js';
import type {
  AmendRequest,
  ClockChangeView,
  CrewCardResult,
  CrewCardUpdate,
  CrewCertificates,
  CrewMedicalInput,
  CrewMedicalResult,
  CrewMember,
  CrewMovementList,
  CrewProfileView,
  CrewRestReport,
  MovementEvent,
  MovementQuery,
  MovementResult,
  NewCrewCardInput,
  PersonnelVessel,
  RankChangeRequest,
  RankOption,
  RestMonth,
  RestMonthDecision,
  SignOffRequest,
  SignOnRequest,
  WorkPeriod,
  WorkPeriodInput,
  WorkPeriodPatch,
} from './types.js';

/**
 * Personel kartı yazma (ADR 0131 §1, §7): yalnız ofiste. Hesapsız kart parola istemez; giriş hesabı
 * işlemi yapanın parolasıyla açılır ve geçici parola bir kez döner.
 */
export interface CrewCardEditor {
  create(input: NewCrewCardInput): Promise<CrewCardResult>;
  update(userId: string, input: CrewCardUpdate): Promise<CrewProfileView>;
  createLogin(userId: string, email: string, password: string): Promise<CrewCardResult>;
  /**
   * Yanlış açılan kartı kaldırır (ADR 0142 §6): hiç görev almamış hesapsız kişi; işlemi yapanın
   * parolası. Eski bağdaştırıcıda yok.
   */
  remove?(userId: string, rowVersion: number, password: string): Promise<void>;
  /**
   * Hesaplı kişiye yeni geçici parola (ortak 2026-10-30): Settings ▸ Users'daki uç
   * (`/office/users/:id/reset-password`), `users` satırının sürümü ve işlemi yapanın parolası.
   * Geçici parola yalnız bu yanıtta bir kez döner. Eski bağdaştırıcıda yok.
   */
  resetPassword?(
    userId: string,
    rowVersion: number,
    password: string,
  ): Promise<{ temporaryPassword: string }>;
  /** Giriş e-postasını düzeltir (ADR 0145; Settings ▸ Users'daki uç). Eski bağdaştırıcıda yok. */
  changeEmail?(
    userId: string,
    rowVersion: number,
    email: string,
    password: string,
  ): Promise<unknown>;
  /**
   * Kartın fotoğrafı (ADR 0158 §1): dosya verilirse konur ya da değiştirilir, `null` kaldırır.
   * Bağdaştırıcı yüklemeden önce küçültür ve boyutu denetler. Eski bağdaştırıcıda yok.
   */
  setPhoto?(userId: string, rowVersion: number, file: File | null): Promise<CrewProfileView>;
  /** Sağlık bilgisi (ADR 0158 §2): her okuma sunucuda kayda geçer. Eski bağdaştırıcıda yok. */
  medical?(userId: string): Promise<CrewMedicalResult>;
  saveMedical?(
    userId: string,
    input: { medical: CrewMedicalInput; rowVersion: number | null },
  ): Promise<CrewMedicalResult>;
  /** "Disable / Enable login" (Settings ▸ Users'daki uçlar, `users.void`). Eski bağdaştırıcıda yok. */
  setLoginActive?(
    userId: string,
    rowVersion: number,
    active: boolean,
    password: string,
  ): Promise<unknown>;
  /** "Turn off two-step verification" (Settings ▸ Users'daki uç). Eski bağdaştırıcıda yok. */
  resetTotp?(userId: string, password: string): Promise<unknown>;
}

/**
 * Joining / Sign-Off yazma (ADR 0131 §3): yalnız ofiste. Gemi listeyi salt okunur görür. Atama
 * silen "Cancel joining" `users.void` ister (Ek 2).
 */
export interface CrewMovementEditor {
  ranks(): Promise<RankOption[]>;
  signOn(input: SignOnRequest): Promise<MovementResult>;
  signOff(assignmentId: string, input: SignOffRequest): Promise<MovementResult>;
  rankChange(assignmentId: string, input: RankChangeRequest): Promise<MovementResult>;
  amend(assignmentId: string, input: AmendRequest): Promise<MovementResult>;
  cancel(
    assignmentId: string,
    input: { rowVersion: number; reason: string },
  ): Promise<MovementResult>;
  revoke(
    assignmentId: string,
    input: { rowVersion: number; reason?: string | null },
  ): Promise<MovementResult>;
  events(assignmentId: string): Promise<MovementEvent[]>;
}

/**
 * Gemide saat girişi ve aylık imza (M10d; ADR 0021). Yalnız gemi kaynağı verir: saatler gemide
 * girilir ve kaptan gemide onaylar [MLC A2.3 §12]; ofis okur (mimari belge §8).
 */
export interface RestHoursEditor {
  addWorkPeriod(input: WorkPeriodInput): Promise<WorkPeriod>;
  updateWorkPeriod(id: string, patch: WorkPeriodPatch): Promise<WorkPeriod>;
  removeWorkPeriod(id: string, rowVersion: number): Promise<void>;
  /** Parola yalnız girildiyse (ADR 0075); verilmezse gövdede alan olmaz. */
  signRestMonth(
    month: string,
    password: string | undefined,
    contentHash: string,
  ): Promise<RestMonth>;
  decideRestMonth(month: string, decision: RestMonthDecision): Promise<RestMonth>;
}

/**
 * Personel ekranlarının veri kaynağı (M6d3): web ofis `fetch` ile ofis API'sine, gemi uygulaması
 * IPC ile ana sürece (oradan pinlenmiş TLS ile gemi sunucusuna) bağlar. Ekran bir kez yazılır,
 * iki uygulamada aynı görünür (ADR 0019 §2, §6). Hata `{ code }` taşıyan bir `Error` olarak gelir.
 */
export interface PersonnelSource {
  /** `web`: filo; `ship`: tek gemi. Belge yazma yalnız ofiste, ekranın `actions` ile (ADR 0016 §8). */
  scope: 'web' | 'ship';
  vessels(): Promise<PersonnelVessel[]>;
  /** `canSignOn`: ofiste New Crew Card ▸ "Sign on now" bölümü (ADR 0131 Ek 4 §2). */
  /** `canCreate`: "+ Add Crew" (`users.create`; ortak 2026-10-30); eski sunucu göndermez. */
  /** `canCreateLogin`: New Crew Card ▸ "Create login" (`users.create` + `users.edit`; ADR 0151 Ek). */
  crew(): Promise<{
    today: string;
    crew: CrewMember[];
    canSignOn?: boolean;
    canCreate?: boolean;
    canCreateLogin?: boolean;
  }>;
  /** Crew Profile; gemide salt okunur ve yalnız inen alanlarla (ADR 0131 §2). */
  crewProfile(userId: string): Promise<CrewProfileView>;
  /** Yalnız ofiste: "+ Add Crew", Edit ve "Create login". */
  crewCards?: CrewCardEditor;
  /** Sign On / Off listesi (tasarım 5); gemide yalnız bu geminin satırları. */
  movements?(query: MovementQuery): Promise<CrewMovementList>;
  /** Yalnız ofiste: Sign On, Sign Off, Rank change, Amend, Cancel, Revoke, geçmiş. */
  movementEditor?: CrewMovementEditor;
  /** Yalnız web ofiste: Crew Profile ▸ Office notes (ADR 0159). Gemi kaynağında yoktur. */
  crewNotes?: CrewNotesSource;
  crewCertificates(userId: string): Promise<CrewCertificates>;
  restHours(
    vesselId: string,
    from: string,
    to: string,
  ): Promise<{
    vessel: { id: string; name: string; timeZone: string };
    crew: CrewRestReport[];
    /** Gemi saati kaydı (ADR 0065 §7): gün sınırları ve çizelge işaretleri; eski sunucu göndermez. */
    clockChanges?: ClockChangeView[];
  }>;
  workPeriods(vesselId: string, userId: string, from: string, to: string): Promise<WorkPeriod[]>;
  /** Kişinin aylık dinlenme kaydı: durum, dönemler, imzalar (ADR 0021). */
  restMonth(vesselId: string, userId: string, month: string): Promise<RestMonth>;
  /** Yalnız gemide (M10d). */
  editor?: RestHoursEditor;
  /**
   * PDF'i kullanıcıya verme yolu (ADR 0024 #12). Verilmezse tarayıcı yolu (indirme ve yeni
   * sekmede yazdırma); gemi uygulaması yazdırmayı ana süreçte yapar.
   */
  pdf?: PdfOutput;
  /** PDF başlığının şirket adı ve logosu (Crew Profile, Record of Employment; ADR 0131 Ek 3). */
  brand?(): Promise<ReportBrand | null>;
  /**
   * Dosyayı kaydettirir (hareket Excel'i). Verilmezse tarayıcı indirmesi; gemide oturum indirmeyi
   * iptal ettiği için ana süreç kaydeder.
   */
  saveFile?(fileName: string, bytes: Uint8Array, type: string): Promise<void>;
}

/** Veri kaynağı hatasının kodu: sunucu kodu ya da genel hata. */
export function errorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : 'GENERIC';
}
